import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminStorageResponse } from '../../shared/types';
import Eyebrow from '../components/Eyebrow';
import {
  AdminFooter,
  AdminShell,
  SectionHeading,
  StatePanel,
  cardClass,
  formatBytes,
  formatDate,
  quietButtonClass,
  quietSmButtonClass,
} from '../components/admin/AdminUi';
import { adminApi, ApiError } from '../lib/api';

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; storage: AdminStorageResponse }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

export default function AdminStoragePage() {
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  useEffect(() => {
    adminApi
      .storage()
      .then((storage) => setState({ kind: 'ready', storage }))
      .catch((caught) => {
        if (caught instanceof ApiError && caught.status === 501) setState({ kind: 'unavailable' });
        else setState({ kind: 'error', message: caught instanceof ApiError ? caught.message : 'We could not check storage.' });
      });
  }, []);

  return (
    <AdminShell>
      <div className="mx-auto max-w-6xl px-5 py-10 md:px-10 md:py-14">
        <header className="rise">
          <SectionHeading
            level="page"
            eyebrow="Storage"
            title="Media storage"
            description="See what is using space and which projects are due to expire soon."
          />
        </header>

        <div className="mt-6">
          {state.kind === 'loading' ? (
            <StatePanel eyebrow="Loading" title="Counting review media"><p>This should only take a moment.</p></StatePanel>
          ) : state.kind === 'unavailable' ? (
            <StatePanel eyebrow="Not available yet" title="Storage reporting is waiting for Phase 1B" tone="tint">
              <p>The media package will provide usage, ceiling, and project totals here. Nothing else in the admin app is blocked.</p>
              <Link to="/admin" className={`${quietButtonClass} mt-5`}>Back to projects</Link>
            </StatePanel>
          ) : state.kind === 'error' ? (
            <StatePanel eyebrow="Could not load" title="We could not check storage" tone="error">
              <p>{state.message} Mind refreshing this page in a moment?</p>
            </StatePanel>
          ) : (
            <StorageDetails
              storage={state.storage}
              onRecalculated={(projectId, storageBytes) => setState((current) => (
                current.kind === 'ready'
                  ? {
                    ...current,
                    storage: {
                      ...current.storage,
                      projects: current.storage.projects.map((project) => (
                        project.project_id === projectId
                          ? { ...project, storage_bytes: storageBytes }
                          : project
                      )),
                    },
                  }
                  : current
              ))}
            />
          )}
        </div>
      </div>
      <AdminFooter path="ss-review / storage" />
    </AdminShell>
  );
}

function StorageDetails({ storage, onRecalculated }: {
  storage: AdminStorageResponse;
  onRecalculated: (projectId: string, storageBytes: number) => void;
}) {
  const usedPercent = storage.ceiling_bytes > 0
    ? Math.min(100, (storage.used_bytes / storage.ceiling_bytes) * 100)
    : 0;
  const largest = useMemo(
    () => [...storage.projects].sort((a, b) => b.storage_bytes - a.storage_bytes).slice(0, 10),
    [storage.projects],
  );
  const upcoming = useMemo(
    () => storage.projects
      .filter((project) => project.expiry_at !== null && project.expiry_at >= Date.now())
      .sort((a, b) => (a.expiry_at ?? 0) - (b.expiry_at ?? 0))
      .slice(0, 10),
    [storage.projects],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className={`${cardClass} rise-subtle p-7 lg:col-span-2`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow accent>Current usage</Eyebrow>
            <p className="mt-3 font-display text-5xl font-semibold leading-none">{formatBytes(storage.used_bytes)}</p>
            <p className="mt-2 text-sm text-muted">
              {formatBytes(storage.available_bytes)} available
              {storage.trash_bytes > 0 ? (
                <span className="text-faint"> · includes {formatBytes(storage.trash_bytes)} in Trash</span>
              ) : null}
            </p>
          </div>
          <p className="text-sm text-muted">Ceiling: {formatBytes(storage.ceiling_bytes)}</p>
        </div>
        <div className="meter mt-6" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(usedPercent)}>
          <div className="meter-fill" style={{ width: `${usedPercent}%` }} />
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{usedPercent.toFixed(1)} per cent used</p>
      </section>

      <ProjectStorageList title="Largest projects" eyebrow="By size" projects={largest} empty="No uploaded media yet." onRecalculated={onRecalculated} />
      <ProjectStorageList title="Upcoming expiries" eyebrow="By date" projects={upcoming} empty="No project expiries are coming up." showExpiry onRecalculated={onRecalculated} />
    </div>
  );
}

/**
 * The storage report reads the maintained storage_bytes column rather than
 * recomputing every project on load, so reconciliation against R2 needs a place
 * to be asked for. This is it.
 */
function RecalculateButton({ projectId, title, onRecalculated }: {
  projectId: string;
  title: string;
  onRecalculated: (projectId: string, storageBytes: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function recalculate() {
    setBusy(true);
    setFailed(false);
    try {
      const result = await adminApi.recalculateStorage(projectId);
      onRecalculated(result.project_id, result.storage_bytes);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={quietSmButtonClass}
      onClick={() => void recalculate()}
      disabled={busy}
      title={`Recheck ${title} against R2`}
    >
      {busy ? 'Rechecking' : failed ? 'Retry' : 'Recheck'}
    </button>
  );
}

function ProjectStorageList({
  title,
  eyebrow,
  projects,
  empty,
  showExpiry = false,
  onRecalculated,
}: {
  title: string;
  eyebrow: string;
  projects: AdminStorageResponse['projects'];
  empty: string;
  showExpiry?: boolean;
  onRecalculated: (projectId: string, storageBytes: number) => void;
}) {
  return (
    <section className={`${cardClass} rise-subtle p-7`}>
      <SectionHeading eyebrow={eyebrow} title={title} />
      {projects.length === 0 ? (
        <p className="mt-6 text-sm text-muted">{empty}</p>
      ) : (
        <ol className="mt-6 divide-y divide-line">
          {projects.map((project) => (
            <li key={project.project_id} className="flex items-center justify-between gap-4 py-4">
              <div>
                <Link to={`/admin/projects/${project.project_id}`} className="font-semibold text-accent">{project.title}</Link>
                {showExpiry ? <p className="mt-1 text-xs text-faint">{formatDate(project.expiry_at)}</p> : null}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">{formatBytes(project.storage_bytes)}</span>
                <RecalculateButton projectId={project.project_id} title={project.title} onRecalculated={onRecalculated} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
